import React, { useState, useMemo } from "react";
import { GivingPanel } from "../components/features/GivingPanel";
import { Users, UserPlus, Upload, Zap, Settings2, LayoutGrid, List, Layers } from "lucide-react";
import { useApp } from "../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../constants/appConfig";
import { ViewLayout } from "../components/ui/ViewLayout";
import { TabBar } from "../components/ui/TabBar";
import { Card } from "../components/ui/Card";
import { ActionButton } from "../components/ui/ActionButton";
import { ZoneOneHeader } from "../components/zones/ZoneHeaderFooter";
import { ZoneThreeFooter } from "../components/zones/ZoneHeaderFooter";
import { ZoneTwoToolbar } from "../components/zones/ZoneTwoToolbar";
import { DonorCard } from "../components/donors/DonorCard";
import { DonorListTable } from "../components/donors/DonorListTable";
import { DonorDeckView } from "../components/donors/DonorDeckView";
import { AddDonorModal } from "../components/features/AddDonorModal";
import { SpektrixImportModal } from "../components/features/SpektrixImportModal";
import { BulkUpdateModal } from "../components/features/BulkUpdateModal";
import { MomentumTicker } from "../components/features/MomentumTicker";
import {
  NewDonorsSidebar,
  NewDonorsToggleButton,
} from "../components/features/NewDonorsSidebar";
import { HELP } from "../constants/helpContent";

// ── Tab Definitions ──────────────────────────────────────────
const DONOR_TABS = [
  { id: "cards", label: "Cards", icon: LayoutGrid },
  { id: "list", label: "List", icon: List },
  { id: "deck", label: "Deck", icon: Layers },
  { id: "manage", label: "Manage", icon: Settings2 },
];

// Helper: count new members (startDate within last 90 days)
const countNewMembers = (members) => {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 90);
  return members.filter((m) => {
    if (!m.startDate) return false;
    const d = new Date(m.startDate);
    return !isNaN(d.getTime()) && d >= cutoff;
  }).length;
};

export const MainView = () => {
  const {
    palette,
    members,
    filteredMembers,
    chapterName,
    viewMode,
    setViewMode,
    sortBy,
    navigate,
    can,
    isAdmin,
    loadFromSupabase,
  } = useApp();

  // viewMode from AppContext persists across navigation (cards/list/deck/manage)
  const activeTab = viewMode || "cards";
  const setActiveTab = setViewMode;
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showBulkUpdate, setShowBulkUpdate] = useState(false);
  const [showNewMembers, setShowNewMembers] = useState(false);

  const newMemberCount = useMemo(() => countNewMembers(members), [members]);

  // Group members by first letter of sort field for alphabet strip
  const displayMembers = filteredMembers;
  const grouped = useMemo(() => {
    const groups = {};
    displayMembers.forEach((member) => {
      const field =
        sortBy === "organization"
          ? member.organization
          : sortBy === "firstName"
          ? member.firstName
          : member.lastName;
      const letter = (field || "?")[0].toUpperCase();
      if (!groups[letter]) groups[letter] = [];
      groups[letter].push(member);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [displayMembers, sortBy]);

  const letters = grouped.map(([letter]) => letter);

  const handleNewMemberClick = (member) => {
    setShowNewMembers(false);
    navigate("memberDetail", member);
  };

  // ── Manage tab card styles ──────────────────────────────────
  const manageCard = {
    padding: "24px 28px",
    borderRadius: 12,
    border: `1px solid ${palette.cardBorder}`,
    background: palette.cardBg,
    cursor: "pointer",
    transition: "all 0.2s",
  };

  return (
    <ViewLayout>
      <ZoneOneHeader
        title="Donors"
        subtitle={`${members.length} donors in ${chapterName}`}
        icon={Users}
        onBack={() => navigate("main")}
        onHome={() => navigate("main")}
        helpContent={HELP.main}
        rightContent={
          activeTab !== "manage" ? (
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <NewDonorsToggleButton
                onClick={() => setShowNewMembers(!showNewMembers)}
                memberCount={newMemberCount}
              />
            </div>
          ) : null
        }
      />

      {/* Zone 2 toolbar — always visible for navigation */}
      <ZoneTwoToolbar />

      {/* Giving quick glance — below toolbar, above tabs */}
      <GivingPanel />
      <div style={{ padding: "10px 0" }}>
        <MomentumTicker />
      </div>

      {/* Tab Bar */}
      <TabBar tabs={DONOR_TABS} activeTab={activeTab} onTabChange={setActiveTab} />

      {/* ══ Empty state (shared) ════════════════════════════════ */}
      {activeTab !== "manage" && displayMembers.length === 0 && (
        <div style={{ flex: 1, textAlign: "center", padding: "60px 20px" }}>
          <Users size={ICON_SIZES.xxl} style={{ color: palette.text3, opacity: 0.4, marginBottom: 16 }} />
          <h3 style={{ color: palette.text1, fontFamily: FONT_FAMILY, margin: "0 0 8px" }}>
            {members.length === 0 ? "No donors yet" : "No donors match your filters"}
          </h3>
          <p style={{ color: palette.text3, fontSize: FONT_SIZES.sm, fontFamily: FONT_FAMILY }}>
            {members.length === 0 ? "Go to the Manage tab to add or import donors" : "Try adjusting your search or filter criteria"}
          </p>
        </div>
      )}

      {/* ══ TAB: Cards ═══════════════════════════════════════════ */}
      {activeTab === "cards" && displayMembers.length > 0 && (
        <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
          {/* Alphabet Strip */}
          {letters.length > 1 && (
            <div
              style={{
                width: 28,
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "12px 0",
                gap: 1,
                overflowY: "auto",
                background: "rgba(0,0,0,0.03)",
              }}
            >
              {letters.map((letter) => (
                <button
                  key={letter}
                  onClick={() =>
                    document
                      .getElementById(`group-${letter}`)
                      ?.scrollIntoView({ behavior: "smooth" })
                  }
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 4,
                    border: "none",
                    background: "transparent",
                    color: palette.text3,
                    fontSize: "0.55rem",
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: FONT_FAMILY,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = `${palette.accent1}15`;
                    e.currentTarget.style.color = palette.accent1;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = palette.text3;
                  }}
                >
                  {letter}
                </button>
              ))}
            </div>
          )}
          <div style={{ flex: 1, padding: 24, overflowY: "auto" }}>
            {grouped.map(([letter, groupMembers]) => (
              <div key={letter} id={`group-${letter}`}>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    marginBottom: 10,
                    marginTop: 12,
                    background: `linear-gradient(135deg, ${palette.accent1}, ${palette.accent2})`,
                    color: "#fff",
                    fontSize: FONT_SIZES.sm,
                    fontWeight: 700,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  {letter}
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))",
                    gap: 16,
                    marginBottom: 8,
                  }}
                >
                  {groupMembers.map((member) => (
                    <DonorCard key={member.id || member.afpId} member={member} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══ TAB: List ════════════════════════════════════════════ */}
      {activeTab === "list" && displayMembers.length > 0 && (
        <div style={{ flex: 1, padding: "0 24px 24px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <DonorListTable members={displayMembers} />
        </div>
      )}

      {/* ══ TAB: Deck ════════════════════════════════════════════ */}
      {activeTab === "deck" && displayMembers.length > 0 && (
        <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
          <DonorDeckView
            members={displayMembers}
            onMemberClick={(m) => navigate("memberDetail", m)}
          />
        </div>
      )}

      {/* ══ TAB: Manage ══════════════════════════════════════════ */}
      {activeTab === "manage" && (
        <div style={{ flex: 1, overflowY: "auto", padding: "24px 28px" }}>
          <p
            style={{
              fontSize: FONT_SIZES.sm,
              color: palette.text2,
              fontFamily: FONT_FAMILY,
              margin: "0 0 20px",
            }}
          >
            Add donors, import from CSV, or update donors in bulk.
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 16,
            }}
          >
            {/* Add Donor */}
            {can("addMembers") && (
              <div
                style={manageCard}
                onClick={() => setShowAdd(true)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = palette.accent1;
                  e.currentTarget.style.boxShadow = `0 4px 12px ${palette.accent1}15`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = palette.cardBorder;
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      background: `${palette.accent1}15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <UserPlus size={ICON_SIZES.md} style={{ color: palette.accent1 }} />
                  </div>
                  <span
                    style={{
                      fontSize: FONT_SIZES.md,
                      fontWeight: 700,
                      color: palette.text1,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    Add Donor
                  </span>
                </div>
                <p
                  style={{
                    fontSize: FONT_SIZES.xs,
                    color: palette.text3,
                    fontFamily: FONT_FAMILY,
                    margin: 0,
                    lineHeight: 1.5,
                  }}
                >
                  Create a new individual, household, or organization donor record manually.
                </p>
              </div>
            )}

            {/* Import */}
            {can("addMembers") && (
              <div
                style={manageCard}
                onClick={() => setShowImport(true)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = palette.accent1;
                  e.currentTarget.style.boxShadow = `0 4px 12px ${palette.accent1}15`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = palette.cardBorder;
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      background: `#10b98115`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Upload size={ICON_SIZES.md} style={{ color: "#10b981" }} />
                  </div>
                  <span
                    style={{
                      fontSize: FONT_SIZES.md,
                      fontWeight: 700,
                      color: palette.text1,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    Import CSV
                  </span>
                </div>
                <p
                  style={{
                    fontSize: FONT_SIZES.xs,
                    color: palette.text3,
                    fontFamily: FONT_FAMILY,
                    margin: 0,
                    lineHeight: 1.5,
                  }}
                >
                  Import donors and gift history from a CSV file. Supports Spektrix and custom formats.
                </p>
              </div>
            )}

            {/* Bulk Update */}
            {isAdmin && (
              <div
                style={manageCard}
                onClick={() => setShowBulkUpdate(true)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = palette.accent1;
                  e.currentTarget.style.boxShadow = `0 4px 12px ${palette.accent1}15`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = palette.cardBorder;
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      background: `#f59e0b15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Zap size={ICON_SIZES.md} style={{ color: "#f59e0b" }} />
                  </div>
                  <span
                    style={{
                      fontSize: FONT_SIZES.md,
                      fontWeight: 700,
                      color: palette.text1,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    Bulk Update
                  </span>
                </div>
                <p
                  style={{
                    fontSize: FONT_SIZES.xs,
                    color: palette.text3,
                    fontFamily: FONT_FAMILY,
                    margin: 0,
                    lineHeight: 1.5,
                  }}
                >
                  Find donors by gift data, pathway, flags, or director — then update them in bulk.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <ZoneThreeFooter />

      {/* Modals — guarded by permission */}
      {can("addMembers") && showAdd && (
        <AddDonorModal onClose={() => setShowAdd(false)} />
      )}
      {can("addMembers") && showImport && (
        <SpektrixImportModal onClose={() => setShowImport(false)} />
      )}
      {isAdmin && showBulkUpdate && (
        <BulkUpdateModal
          onClose={() => setShowBulkUpdate(false)}
          onComplete={() => {
            loadFromSupabase();
          }}
        />
      )}

      {/* New Members Sidebar */}
      <NewDonorsSidebar
        isOpen={showNewMembers}
        onClose={() => setShowNewMembers(false)}
        onMemberClick={handleNewMemberClick}
      />
    </ViewLayout>
  );
};
